import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
} from '@angular/forms';
import { createControl } from 'projects/money-movement-ui/src/app/create-request/utilities/formUtilities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Tooltip } from '../../../models/Tooltip';
import { datePickerErrorMap } from '../../../utilities/Constants';
import { TransactionType } from '../../../types/Transaction';

@Component({
  standalone: false,
  selector: 'mm-date-picker-control',
  templateUrl: './date-picker-control.component.html',
  styleUrls: ['./date-picker-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DatePickerControlComponent,
    },
  ],
})
export class DatePickerControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  date = '';
  @Input() label!: string;
  @Input() parentForm!: UntypedFormGroup;
  @Input() id: string;
  @Input() tooltip: Tooltip;
  @Input() formControlName: string;
  @Input() formControlValue: string;
  @Input() formControlValidators: ValidatorFn[];
  @Input() minDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  @Input() maxDate: Date = new Date(
    new Date(3000, 11, 31).setHours(0, 0, 0, 0)
  );
  @Input() disableWeekends: boolean;
  @Input() disableDateSelect: boolean;
  @Input() showIntlWireTooltip: boolean;
  @Input() showOptionalLabel: boolean = false;
  @Input() transactionType: TransactionType;
  @Input() defaultDate: string;
  @Output() newValueEvent = new EventEmitter<string>();
  @Output() showSufficientFundsWarningEvent = new EventEmitter<boolean>();
  onChange = (date: string) => { };
  onTouched = () => { };
  touched = false;
  disabled = false;

  controlValidation$: Subject<{ hasError: boolean; errorText: string }> = new Subject();
  private destroy$ = new Subject<void>();

  constructor(private fb: UntypedFormBuilder) { }

  ngOnInit(): void {
    createControl(this.parentForm, this.formControlName ? this.formControlName : 'selectedDate', this.formControlValue, this.formControlValidators);
    this.setDate(this.control.value);

    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.control?.invalid && this.control?.errors && this.control?.dirty) {
        const errorKeys: string[] = Object.keys(this.control.errors ?? {});
        this.controlValidation$.next({
          hasError: true,
          errorText: this.getErrorText(errorKeys[0])
        });
      } else {
        this.controlValidation$.next({
          hasError: false,
          errorText: '',
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.parentForm?.removeControl(this.formControlName ? this.formControlName : 'selectedDate')
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  get control(): UntypedFormControl {
    return this.parentForm.get(this.formControlName ? this.formControlName : 'selectedDate') as UntypedFormControl;
  }

  writeValue(date: string): void {
    this.date = date;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  markAsDirty() {
    this.control.markAsDirty();
  }

  setDate(date: string) {
    this.date = date;
    this.onDateUpdated();
  }

  onDateUpdated() {
    this.markAsTouched();
    this.markAsDirty();
    this.control.setValue(this.date)
    this.control.updateValueAndValidity();
  }

  getErrorText(error: string) : string {
    if (error === 'dateLessThanMin') {
      return `${datePickerErrorMap[error as keyof typeof datePickerErrorMap]} ${this.defaultDate}`
    } else {
      return `${this.label} ${datePickerErrorMap[error as keyof typeof datePickerErrorMap]}`
    }
  }

  setShowSufficientFundsWarning(showSufficientFundsWarning: boolean) : void {
    this.showSufficientFundsWarningEvent.emit(showSufficientFundsWarning);
  }
}